import { ExternalLink, ShieldCheck } from "lucide-react";
import { hrs } from "@/lib/clipboard";
import { HAZARD_BASE, HAZARD_LABEL } from "@/lib/hazard";
import { OUTCOME } from "@/components/AlertPolicy";

const TH = "text-left text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/85 font-normal py-2 px-3 whitespace-nowrap";
const TD = "py-2.5 px-3 text-[11px] font-mono align-top";
const EXTRA = {
  grace: { label: "In reporting grace", text: "text-sky-300", border: "border-sky-300/50" },
  pending: { label: "Window open", text: "text-atlas-cyan", border: "border-atlas-cyan/50" },
};
const style = (k) => EXTRA[k] || OUTCOME[k] || EXTRA.pending;
const when = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");
const chip = (on) => `h-7 px-2.5 border text-[10px] font-mono uppercase tracking-widest transition-colors ${on ? "border-white/40 text-white bg-white/10" : "border-white/10 text-atlas-secondary hover:text-white"}`;

export const LedgerFilters = ({ d, hazard, setHazard, outcome, setOutcome }) => {
  const hazards = [...new Set(d.rows.map((r) => r.hazard))].sort();
  return (
    <div className="flex flex-col gap-2" data-testid="scoring-filters">
      <div className="flex flex-wrap gap-1.5">
        <button className={chip(!outcome)} onClick={() => setOutcome("")} data-testid="filter-outcome-all">All {d.total}</button>
        {Object.entries(d.labels).map(([k, label]) => (
          <button key={k} className={`${chip(outcome === k)} ${style(k).text}`} onClick={() => setOutcome(k)} data-testid={`filter-outcome-${k}`}>
            {label} {d.counts[k] || 0}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button className={chip(!hazard)} onClick={() => setHazard("")} data-testid="filter-hazard-all">All hazards</button>
        {hazards.map((h) => (
          <button key={h} className={chip(hazard === h)} onClick={() => setHazard(h)} data-testid={`filter-hazard-${h}`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: HAZARD_BASE[h] }} />{HAZARD_LABEL[h] || h}
          </button>
        ))}
      </div>
    </div>
  );
};

const Evidence = ({ ev }) => {
  if (!ev) return <span className="text-atlas-secondary/75">—</span>;
  const label = `${ev.source || "Agency"}${ev.id ? ` · ${ev.id}` : ""}`;
  return ev.link ? (
    <a href={ev.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-atlas-cyan hover:text-white" title={ev.title}>
      {label} <ExternalLink className="w-3 h-3" />
    </a>
  ) : <span className="text-white/80" title={ev.title}>{label}</span>;
};

const Row = ({ r }) => {
  const s = style(r.outcome);
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`ledger-row-${r.uid}`}>
      <td className={`${TD} text-atlas-secondary whitespace-nowrap`}>{when(r.flagged_at)}</td>
      <td className={TD}>
        <div className="text-[9px] uppercase" style={{ color: HAZARD_BASE[r.hazard] }}>{HAZARD_LABEL[r.hazard] || r.hazard}</div>
        <div className="text-white/90 max-w-[260px]" title={r.note || ""}>{r.region}</div>
      </td>
      <td className={TD}>
        <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 whitespace-nowrap ${s.text} ${s.border}`} data-testid={`ledger-outcome-${r.uid}`}>{r.outcome_label}</span>
        {r.verification === "manual" && (
          <div className="mt-1" title={r.manual_note || "Official agency report logged by hand, same window rules"}>
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border border-fuchsia-300/50 text-fuchsia-300 bg-fuchsia-300/5 px-1.5 py-0.5 whitespace-nowrap" data-testid={`ledger-manual-${r.uid}`}><ShieldCheck className="w-3 h-3" />Manually verified</span>
          </div>
        )}
        {r.late_until && <div className="text-[9px] text-atlas-secondary/85 mt-1">watch until {when(r.late_until)}</div>}
        {(r.outcome === "pending" || r.outcome === "grace") && <div className="text-[9px] text-atlas-secondary/85 mt-1">final {when(r.final_at)}</div>}
      </td>
      <td className={TD} data-testid={`ledger-evidence-${r.uid}`}><Evidence ev={r.evidence} /></td>
      <td className={`${TD} text-atlas-secondary hidden md:table-cell`}>{r.basin || "—"}</td>
      <td className={`${TD} text-right text-atlas-cyan whitespace-nowrap`} data-testid={`ledger-lead-${r.uid}`}>{hrs(r.lead_hours)}</td>
    </tr>
  );
};

export const LedgerTable = ({ rows }) => (
  <div className="border border-white/10 bg-atlas-surface/70 overflow-x-auto" data-testid="scoring-table">
    <table className="w-full">
      <thead><tr className="border-b border-white/10">
        <th className={TH}>Flagged (UTC)</th><th className={TH}>Hazard · region</th><th className={TH}>Outcome</th>
        <th className={TH}>Evidence</th><th className={`${TH} hidden md:table-cell`}>Basin</th><th className={`${TH} text-right`}>Lead</th>
      </tr></thead>
      <tbody>{rows.map((r) => <Row key={r.uid} r={r} />)}</tbody>
    </table>
    {rows.length === 0 && <div className="text-[11px] font-mono text-atlas-secondary/80 text-center py-8" data-testid="scoring-no-match">No predictions match these filters.</div>}
  </div>
);
